import React, { Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Layout from '@/components/layout/Layout'
import { AdminLayout } from '@/components/layout/AdminLayout'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { AuthProvider, useAuth } from '@/hooks/useAuth'
import { ThemeProvider } from '@/components/providers/ThemeProvider'
import { AuthGuard } from '@/components/auth/AuthGuard'
import { HRAuthGuard } from '@/components/auth/HRAuthGuard'
import { Toaster } from '@/components/ui/toaster'
import { SessionTimeoutDialog } from '@/components/ui/SessionTimeoutDialog'
import { AuthRedirect } from '@/components/routing/AuthRedirect'

// Lazy load pages for better performance
const ApplicationForm = React.lazy(() => import('@/pages/applicant/ApplicationForm'))
const ApplicationReview = React.lazy(() => import('@/pages/applicant/ApplicationReview'))
const ApplicationSuccess = React.lazy(() => import('@/pages/applicant/ApplicationSuccess'))
const ApplicationStatus = React.lazy(() => import('@/pages/applicant/ApplicationStatus'))
const MagicLinkLogin = React.lazy(() => import('@/pages/auth/MagicLinkLogin'))
const MagicLinkVerify = React.lazy(() => import('@/pages/auth/MagicLinkVerify'))
const Dashboard = React.lazy(() => import('@/pages/Dashboard'))
const HRLogin = React.lazy(() => import('@/pages/admin/HRLogin'))
const HRDashboard = React.lazy(() => import('@/pages/admin/HRDashboard'))
const ApplicationDetail = React.lazy(() => import('@/pages/admin/ApplicationDetail'))
const CompanyManagement = React.lazy(() => import('@/pages/admin/components/company'))
const UserManagement = React.lazy(() => import('@/pages/admin/UserManagement'))
const ApplicantManagement = React.lazy(() => import('@/pages/admin/ApplicantManagement'))
const ApplicationManagement = React.lazy(() => import('@/pages/admin/ApplicationManagement'))
const PermissionManagement = React.lazy(() => import('@/pages/admin/PermissionManagement'))
const NotFoundPage = React.lazy(() => import('@/pages/NotFoundPage'))
const StepSelectorDemo = React.lazy(() => import('@/pages/StepSelectorDemo'))
// const ComponentDemo = React.lazy(() => import('@/pages/ComponentDemo'))
// const DatePickerDemo = React.lazy(() => import('@/pages/DatePickerDemo'))
// const TranslationTest = React.lazy(() => import('@/pages/TranslationTest'))

// Inner component that uses auth context
function AppContent() {
  const { i18n } = useTranslation()
  const { isSessionExpired, clearSessionTimeout, userType } = useAuth()

  return (
    <div className={`min-h-screen bg-background ${i18n.language === 'th' ? 'font-thai' : 'font-sans'}`}>
      <Layout>
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<AuthRedirect />} />
              
              {/* Protected Routes */}
              {/* Redirect /application to /application/step/1 */}
              <Route path="/application" element={<Navigate to="/application/step/1" replace />} />
              
              {/* Step-based application form */}
              <Route path="/application/step/:stepId" element={
                <AuthGuard>
                  <ApplicationForm />
                </AuthGuard>
              } />
              
              {/* Legacy routes - redirect to step-based URLs */}
              <Route path="/apply" element={<Navigate to="/application/step/1" replace />} />
              <Route path="/companies/:companyId/apply" element={<Navigate to="/application/step/1" replace />} />
              <Route path="/application/success" element={
                <AuthGuard>
                  <ApplicationSuccess />
                </AuthGuard>
              } />
              <Route path="/application/review" element={
                <AuthGuard>
                  <ApplicationReview />
                </AuthGuard>
              } />
              <Route path="/application/:id/review" element={<ApplicationReview />} />
              <Route path="/application/:id/status" element={<ApplicationStatus />} />
              
              {/* Demo Routes - Commented out for now */}
              {/* <Route path="/demo" element={<ComponentDemo />} /> */}
              <Route path="/step-selector-demo" element={<StepSelectorDemo />} />
              
              {/* Translation Test Route - Commented out for now */}
              {/* <Route path="/translation-test" element={<TranslationTest />} /> */}
              
              {/* DatePicker Demo Route - Commented out for now */}
              {/* <Route path="/date-demo" element={<DatePickerDemo />} /> */}
              
              {/* Auth Routes */}
              <Route path="/auth" element={<Navigate to="/auth/magic-link" replace />} />
              <Route path="/auth/magic-link" element={<MagicLinkLogin />} />
              <Route path="/auth/verify" element={<MagicLinkVerify />} />
              
              {/* Authenticated Routes */}
              <Route path="/dashboard" element={<Dashboard />} />
              {/* Admin Login */}
              <Route path="/admin/login" element={<HRLogin />} />
              {/* HR Admin Routes with AdminLayout */}
              <Route path="/admin/*" element={
                <HRAuthGuard>
                  <AdminLayout />
                </HRAuthGuard>
              }>
                {/* Nested admin routes */}
                <Route index element={<HRDashboard />} />
                <Route path="companies" element={<CompanyManagement />} />
                <Route path="users" element={<UserManagement />} />
                <Route path="applicants" element={<ApplicantManagement />} />
                <Route path="applications" element={<ApplicationManagement />} />
                <Route path="applications/:id" element={<ApplicationDetail />} />
                <Route path="permissions" element={<PermissionManagement />} />
                <Route path="settings" element={<div>Settings - TODO</div>} />
              </Route>
              
              {/* 404 - Catch all unmatched routes */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </Layout>
        
        {/* Only show session timeout dialog for candidate users */}
        {userType === 'applicant' && (
          <SessionTimeoutDialog 
            open={isSessionExpired} 
            onClose={clearSessionTimeout} 
          />
        )}
        
        <Toaster />
      </div>
    )
}

function App() {
  return (
    <ThemeProvider defaultTheme="light" storageKey="regis-form-ui-theme">
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  )
}

export default App
